import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginScreen from "@/pages/LoginScreen";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/components/layout/DashboardLayout";
import VaultView from "@/pages/VaultView";
import TeamPage from "@/pages/TeamPage";
import AuditLogPage from "@/pages/AuditLogPage";
import { Toaster } from "@/components/ui/sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#090d16] grid-overlay">
    <div className="flex flex-col items-center gap-4 p-8 rounded-2xl bg-[#111726] border border-slate-800">
      <ShieldCheck className="w-10 h-10 text-emerald-400" />
      <div className="flex items-center gap-3 text-slate-200">
        <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
        <span className="font-mono text-sm">Yükleniyor...</span>
      </div>
    </div>
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Synchronous OAuth callback detection - must run before ProtectedRoute
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/dashboard"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<VaultView mode="all" />} />
        <Route path="favorites" element={<VaultView mode="favorites" />} />
        <Route path="category/:categoryId" element={<VaultView mode="category" />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="audit" element={<AuditLogPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              className: "bg-[#111726] border border-slate-800 text-slate-100",
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
